import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Upload, UserPlus, Wand2 } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Spinner } from "@/components/ui";
import SupplierRateExtractionReview, {
  ExtractedLine, ExtractedCardMetadata, ExtractedSupplierContact, ExtractionPayload,
} from "./SupplierRateExtractionReview";

// =============================================================================
// RateCardImportScreen — /rates/import-pdf  (T-008 + T-187)
// Manager picks existing supplier OR uploads a PDF and lets the AI auto-fill
// a new supplier record, then reviews extracted rate lines, then saves.
//
// Flow:
//   1. (optional) pick existing supplier from dropdown
//   2. upload PDF → /sign-upload → R2 PUT → /ai/extract-supplier-rates
//   3. if no existing supplier picked, render a "Confirm supplier" panel
//      pre-filled from the AI's supplier_contact block. Fuzzy-match against
//      existing suppliers by ABN exact, then legal_name lower-substring;
//      offer "Use existing" instead of "Create new" if a match is found.
//   4. review extracted lines in the table
//   5. on Save: if creating new supplier, insert that first; then rate_card;
//      then bulk-insert rate_lines.
//
// Soft validation: NULL category/unit fall back to 'other'/'each' on save.
// =============================================================================

type Phase = "pick_supplier" | "uploading" | "extracting" | "reviewing" | "saving";

interface SupplierRow {
  supplier_id: string;
  legal_name: string;
  trading_name: string | null;
  supplier_code: string;
  abn: string | null;
}

interface NewSupplierDraft {
  legal_name: string;
  trading_name: string;
  abn: string;
  primary_contact_name: string;
  primary_contact_email: string;
  primary_contact_phone: string;
  billing_email: string;
  address_line1: string;
  suburb: string;
  state: string;
  postcode: string;
  payment_terms_days: string; // string for input ergonomics, parsed on save
}

const EMPTY_DRAFT: NewSupplierDraft = {
  legal_name: "", trading_name: "", abn: "",
  primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
  billing_email: "",
  address_line1: "", suburb: "", state: "", postcode: "",
  payment_terms_days: "",
};

function digitsOnly(s: string): string { return s.replace(/\D/g, ""); }

function draftFromExtraction(
  contact: ExtractedSupplierContact | undefined,
  fallbackName: string | undefined,
  abnFromMetadata: string | undefined,
): NewSupplierDraft {
  return {
    legal_name:           contact?.legal_name      ?? fallbackName ?? "",
    trading_name:         contact?.trading_name    ?? "",
    abn:                  digitsOnly(abnFromMetadata ?? ""),
    primary_contact_name: contact?.primary_contact_name  ?? "",
    primary_contact_email:contact?.primary_contact_email ?? "",
    primary_contact_phone:contact?.primary_contact_phone ?? "",
    billing_email:        contact?.billing_email   ?? "",
    address_line1:        contact?.address_line1   ?? "",
    suburb:               contact?.suburb          ?? "",
    state:                contact?.state           ?? "",
    postcode:             contact?.postcode        ?? "",
    payment_terms_days:   contact?.payment_terms_days_detected != null
      ? String(contact.payment_terms_days_detected) : "",
  };
}

/** Fuzzy match suggestion: ABN exact wins; otherwise legal_name lowercase substring. */
function findExistingMatch(
  suppliers: SupplierRow[],
  draft: NewSupplierDraft,
): SupplierRow | null {
  const abn = digitsOnly(draft.abn);
  if (abn.length === 11) {
    const byAbn = suppliers.find(s => digitsOnly(s.abn ?? "") === abn);
    if (byAbn) return byAbn;
  }
  const tn = draft.trading_name.trim().toLowerCase();
  const ln = draft.legal_name.trim().toLowerCase();
  const candidates = [tn, ln].filter(s => s.length >= 4);
  if (candidates.length === 0) return null;
  return suppliers.find(s => {
    const sln = s.legal_name.toLowerCase();
    const stn = (s.trading_name ?? "").toLowerCase();
    return candidates.some(c => sln.includes(c) || (stn && stn.includes(c)));
  }) ?? null;
}

export default function RateCardImportScreen() {
  const user = useRequireAuth();
  if (!user) return null;
  const navigate = useNavigate();
  const canManage = hasRole(user, "manager");

  const [supplierId, setSupplierId]   = useState<string>(""); // existing-supplier path
  const [phase, setPhase]             = useState<Phase>("pick_supplier");
  const [error, setError]             = useState<string | null>(null);
  const [extracted, setExtracted]     = useState<ExtractionPayload | null>(null);

  // T-187 — auto-create supplier path
  const [creatingNew, setCreatingNew] = useState<boolean>(false);
  const [draft, setDraft]             = useState<NewSupplierDraft>(EMPTY_DRAFT);
  const [matchAcknowledged, setMatchAcknowledged] = useState<boolean>(false);

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers_for_rate_card_import"],
    enabled: canManage,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("supplier_id, legal_name, trading_name, supplier_code, abn")
        .eq("is_active", true)
        .order("legal_name");
      if (error) throw error;
      return (data ?? []) as unknown as SupplierRow[];
    },
  });

  // Fuzzy match — recomputed when the draft changes.
  const fuzzyMatch = useMemo(
    () => creatingNew ? findExistingMatch(suppliers, draft) : null,
    [creatingNew, suppliers, draft],
  );

  async function handleFile(file: File) {
    if (file.type !== "application/pdf") {
      setError("PDF only.");
      return;
    }
    setError(null);
    setPhase("uploading");

    try {
      const ts = Date.now();
      const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_").slice(0, 80);
      const key = `rate-card-imports/${user!.user_id}/${ts}_${safeName}`;

      const signRes = await apiFetch("/sign-upload", {
        method: "POST",
        body: JSON.stringify({
          object_key: key,
          bucket: "coretel-prod",
          content_type: "application/pdf",
        }),
      });
      if (!signRes.ok) {
        const t = await signRes.text().catch(() => "");
        throw new Error(`sign-upload failed (${signRes.status}): ${t}`);
      }
      const { upload_url, object_key } = await signRes.json() as { upload_url: string; object_key: string };

      const putRes = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "content-type": "application/pdf" },
      });
      if (!putRes.ok) {
        throw new Error(`R2 PUT failed (${putRes.status})`);
      }

      setPhase("extracting");
      const extractRes = await apiFetch("/ai/extract-supplier-rates", {
        method: "POST",
        body: JSON.stringify({ object_key }),
      });
      if (!extractRes.ok) {
        const t = await extractRes.text().catch(() => "");
        throw new Error(`extraction failed (${extractRes.status}): ${t}`);
      }
      const payload = await extractRes.json() as { extracted: ExtractionPayload };
      setExtracted(payload.extracted);

      // If no supplier picked, prep the draft from AI metadata.
      if (!supplierId) {
        const meta = payload.extracted.card_metadata;
        setDraft(draftFromExtraction(meta.supplier_contact, meta.supplier_name_detected, meta.supplier_abn));
        setCreatingNew(true);
        setMatchAcknowledged(false);
      }

      setPhase("reviewing");
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setPhase("pick_supplier");
    }
  }

  async function handleConfirm(lines: ExtractedLine[], metadata: ExtractedCardMetadata) {
    setPhase("saving");
    setError(null);
    try {
      // STEP 1 — resolve supplier_id
      let resolvedSupplierId = supplierId;
      let resolvedSupplierLegal: string | undefined;
      let resolvedSupplierTrading: string | null | undefined;

      if (!resolvedSupplierId) {
        // Creating new (or user picked the fuzzy match)
        if (fuzzyMatch && !matchAcknowledged) {
          throw new Error(
            `An existing supplier looks like a match: ${fuzzyMatch.legal_name}. ` +
            `Confirm "Use this existing supplier" or "Create new anyway" before saving.`,
          );
        }

        if (!draft.legal_name.trim()) {
          throw new Error("Legal name required for the new supplier.");
        }

        // Generate supplier_code (slug + year + 4-digit timestamp suffix for uniqueness)
        const slug = (draft.trading_name || draft.legal_name)
          .toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24) || "SUPPLIER";
        const supplier_code = `${slug}-${String(Date.now()).slice(-6)}`;
        const termsParsed = draft.payment_terms_days.trim() === ""
          ? 30 : Math.max(0, parseInt(draft.payment_terms_days, 10) || 30);

        const { data: newSup, error: supErr } = await supabase
          .from("suppliers")
          .insert({
            supplier_code,
            legal_name:   draft.legal_name.trim(),
            trading_name: draft.trading_name.trim() || null,
            abn:          digitsOnly(draft.abn) || null,
            primary_contact_name:  draft.primary_contact_name.trim() || null,
            primary_contact_email: draft.primary_contact_email.trim() || null,
            primary_contact_phone: draft.primary_contact_phone.trim() || null,
            billing_email:         draft.billing_email.trim() || null,
            address_line1: draft.address_line1.trim() || null,
            suburb:        draft.suburb.trim() || null,
            state:         draft.state.trim() || null,
            postcode:      draft.postcode.trim() || null,
            payment_terms_days: termsParsed,
            is_active:   true,
            is_approved: true,
            notes:       `Auto-created from rate-card PDF import on ${new Date().toISOString().slice(0, 10)}.`,
          } as any)
          .select("supplier_id, legal_name, trading_name")
          .single();
        if (supErr || !newSup) throw new Error(`create supplier failed: ${supErr?.message}`);

        resolvedSupplierId      = newSup.supplier_id;
        resolvedSupplierLegal   = newSup.legal_name;
        resolvedSupplierTrading = newSup.trading_name;
      } else {
        const found = suppliers.find(s => s.supplier_id === resolvedSupplierId);
        resolvedSupplierLegal   = found?.legal_name;
        resolvedSupplierTrading = found?.trading_name;
      }

      // STEP 2 — create rate_card
      const yearStr = String(new Date().getFullYear());
      const slugBase = (resolvedSupplierTrading ?? resolvedSupplierLegal ?? "SUPPLIER")
        .toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24);
      const rate_card_code = `${slugBase}-SOR-${yearStr}-${String(Date.now()).slice(-4)}`;

      const { data: card, error: cardErr } = await supabase
        .from("rate_cards")
        .insert({
          rate_card_code,
          display_name:    metadata.card_title?.trim()
            ? metadata.card_title.trim()
            : `${resolvedSupplierTrading ?? resolvedSupplierLegal ?? "Supplier"} — ${yearStr}`,
          rate_card_type:  "supplier_sor",
          supplier_id:     resolvedSupplierId,
          effective_from:  metadata.effective_from || new Date().toISOString().slice(0, 10),
          effective_until: metadata.effective_until || null,
          is_default:      false,
          is_active:       true,
          notes:
            `Imported from PDF on ${new Date().toISOString().slice(0, 10)}. ` +
            `GST treatment as imported: ${metadata.gst_treatment}.`,
        } as any)
        .select("rate_card_id")
        .single();
      if (cardErr || !card) throw new Error(`create card failed: ${cardErr?.message}`);

      // STEP 3 — bulk insert rate_lines (de-dup line_codes within payload)
      const seen = new Set<string>();
      const rows = lines.map((l, idx) => {
        let code = (l.line_code || `LINE-${idx + 1}`).toUpperCase();
        let s = 0;
        while (seen.has(code)) { s++; code = `${(l.line_code || "LINE")}-${s}`.toUpperCase(); }
        seen.add(code);
        return {
          rate_card_id:      card.rate_card_id,
          line_code:         code,
          display_name:      l.display_name?.trim() || code,
          category:          l.category ?? "other",
          unit:              l.unit ?? "each",
          cost_price_ex_gst: l.cost_price_ex_gst ?? 0,
          sell_price_ex_gst: 0,
          notes:             l.notes?.trim() || null,
          sort_order:        idx,
          is_active:         true,
        };
      });
      const { error: linesErr } = await supabase.from("rate_lines").insert(rows as any);
      if (linesErr) throw new Error(`insert lines failed: ${linesErr.message}`);

      navigate(`/rates/${card.rate_card_id}`);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setPhase("reviewing");
    }
  }

  if (!canManage) {
    return (
      <div className="p-6 max-w-[700px] mx-auto">
        <Card><div className="p-6 text-text-muted">Manager+ required.</div></Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <Link to="/rates" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Rate Cards
      </Link>
      <header className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight inline-flex items-center gap-2">
          <Wand2 size={22}/> Import supplier rate card from PDF
        </h1>
        <div className="text-text-muted text-[13px] mt-1">
          Pick an existing supplier or upload a PDF and let Claude auto-detect
          the supplier from the header. Costs and units stay editable —
          if a category or unit isn't right, change it inline.
        </div>
      </header>

      {error && (
        <div className="mb-4 text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2">
          {error}
        </div>
      )}

      <Card>
        <div className="p-5 space-y-4">
          <Field label="Existing supplier (optional — leave blank to auto-create from PDF)">
            <Select
              value={supplierId}
              onChange={e => setSupplierId(e.target.value)}
              disabled={phase !== "pick_supplier"}
            >
              <option value="">— auto-detect from PDF —</option>
              {suppliers.map(s => (
                <option key={s.supplier_id} value={s.supplier_id}>
                  {s.legal_name}{s.trading_name && ` (${s.trading_name})`}
                </option>
              ))}
            </Select>
          </Field>

          {phase === "pick_supplier" && (
            <Field label="PDF">
              <label className="block">
                <div className="border-2 border-dashed border-line hover:border-accent rounded p-6 text-center cursor-pointer"
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }} onDragEnter={e => e.preventDefault()} 
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}>
                  <Upload size={20} className="inline-block mr-2"/>
                  Drop PDF here or click to choose
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
                  />
                </div>
              </label>
              <div className="text-[11px] text-text-muted mt-2">
                If you didn't pick a supplier above, Claude will auto-fill a new
                supplier record from the PDF header — you'll review it before save.
              </div>
            </Field>
          )}

          {(phase === "uploading" || phase === "extracting") && (
            <div className="py-10 flex flex-col items-center gap-3 text-text-muted">
              <Spinner />
              <div className="text-[12px]">
                {phase === "uploading" ? "Uploading PDF…" : "Claude is reading the rate card…"}
              </div>
            </div>
          )}

          {phase === "reviewing" && extracted && (
            <div className="space-y-4">
              {/* T-187 — supplier confirm/create panel, only when no supplier was preselected */}
              {creatingNew && (
                <Card>
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <UserPlus size={16} className="text-accent"/>
                      <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider">
                        Supplier — auto-detected from PDF
                      </div>
                    </div>

                    {fuzzyMatch && (
                      <div className="rounded border border-amber-400/30 bg-amber-400/[0.05] px-3 py-2 text-[12px]">
                        <div className="font-medium mb-1">Possible match in your supplier list:</div>
                        <div className="mb-2 text-text"><strong>{fuzzyMatch.legal_name}</strong> {fuzzyMatch.trading_name && `(${fuzzyMatch.trading_name})`}</div>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={CheckCircle2}
                            onClick={() => {
                              setSupplierId(fuzzyMatch.supplier_id);
                              setCreatingNew(false);
                              setMatchAcknowledged(true);
                            }}
                          >Use existing</Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMatchAcknowledged(true)}
                          >Create new anyway</Button>
                        </div>
                        {matchAcknowledged && (
                          <div className="text-[11px] text-text-muted mt-2">
                            Acknowledged — will create a new supplier on save (this may produce a duplicate).
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-3">
                      <Field label="Legal name *">
                        <Input value={draft.legal_name} onChange={e => setDraft(d => ({ ...d, legal_name: e.target.value }))}/>
                      </Field>
                      <Field label="Trading name">
                        <Input value={draft.trading_name} onChange={e => setDraft(d => ({ ...d, trading_name: e.target.value }))}/>
                      </Field>
                      <Field label="ABN" hint="11 digits, no spaces">
                        <Input
                          value={draft.abn}
                          mono
                          onChange={e => setDraft(d => ({ ...d, abn: digitsOnly(e.target.value).slice(0, 11) }))}
                        />
                      </Field>
                      <Field label="Payment terms (days)">
                        <Input
                          type="number"
                          value={draft.payment_terms_days}
                          onChange={e => setDraft(d => ({ ...d, payment_terms_days: e.target.value }))}
                          placeholder="30"
                        />
                      </Field>
                      <Field label="Primary contact name">
                        <Input value={draft.primary_contact_name} onChange={e => setDraft(d => ({ ...d, primary_contact_name: e.target.value }))}/>
                      </Field>
                      <Field label="Primary contact email">
                        <Input value={draft.primary_contact_email} onChange={e => setDraft(d => ({ ...d, primary_contact_email: e.target.value }))}/>
                      </Field>
                      <Field label="Primary contact phone">
                        <Input value={draft.primary_contact_phone} onChange={e => setDraft(d => ({ ...d, primary_contact_phone: e.target.value }))}/>
                      </Field>
                      <Field label="Billing / accounts email">
                        <Input value={draft.billing_email} onChange={e => setDraft(d => ({ ...d, billing_email: e.target.value }))}/>
                      </Field>
                      <Field label="Address line 1">
                        <Input value={draft.address_line1} onChange={e => setDraft(d => ({ ...d, address_line1: e.target.value }))}/>
                      </Field>
                      <div className="grid grid-cols-3 gap-2">
                        <Field label="Suburb">
                          <Input value={draft.suburb} onChange={e => setDraft(d => ({ ...d, suburb: e.target.value }))}/>
                        </Field>
                        <Field label="State">
                          <Input value={draft.state} onChange={e => setDraft(d => ({ ...d, state: e.target.value.toUpperCase().slice(0, 3) }))}/>
                        </Field>
                        <Field label="Postcode">
                          <Input value={draft.postcode} onChange={e => setDraft(d => ({ ...d, postcode: digitsOnly(e.target.value).slice(0, 4) }))}/>
                        </Field>
                      </div>
                    </div>
                    <div className="text-[11px] text-text-muted">
                      A new supplier record will be created on Save. ABN, contact, address are optional but help with future invoicing.
                    </div>
                  </div>
                </Card>
              )}

              <SupplierRateExtractionReview
                payload={extracted}
                onConfirm={handleConfirm}
                onCancel={() => { setPhase("pick_supplier"); setExtracted(null); setCreatingNew(false); }}
                confirmLabel="Save as rate card"
              />
            </div>
          )}

          {phase === "saving" && (
            <div className="py-6 flex items-center justify-center gap-2 text-text-muted">
              <Spinner /> Saving…
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
